/**
 * Centralized page-title registry + resolver for the whole app.
 *
 * - `ROUTE_TITLES` maps a route pattern to a human-readable title.
 *   Patterns use the same `:id` placeholders as react-router, e.g.
 *   `/clients/update/:id`.
 *
 * - `getPageTitle(pathname)` looks up the best match for an actual URL path
 *   (e.g. `/clients/update/643...`). Resolution order:
 *     1) exact pattern match
 *     2) replace id-like segments (24-char hex, UUIDs, numeric) with `:id`
 *        and try again
 *     3) progressively strip trailing segments until a match is found
 *     4) humanize the first path segment as a last-resort fallback
 *
 * - `getBrandName()` returns the product name from the current build theme.
 *
 * - `formatDocumentTitle(pageTitle)` returns the string actually written to
 *   `document.title`. Page title comes FIRST so narrow browser tabs still
 *   show the page name before it gets truncated.
 *
 * To add or change a title, just edit `ROUTE_TITLES` below.
 */

// Route pattern → page title
export const ROUTE_TITLES = {
  // Core
  '/': 'Dashboard',
  '/login': 'Login',
  '/accept-terms': 'Accept Terms of Service',
  '/legal/terms': 'Terms of Service',
  '/legal/privacy': 'Privacy Policy',
  '/logout': 'Logging Out',
  '/search': 'Search Results',
  '/clock': 'Clock',
  '/access-control': 'Access Control',
  '/admincontrol': 'Admin Control',

  // Super admin
  '/apikeys': 'API Keys',
  '/apikeys/create': 'Create API Key',
  '/apikeys/update/:id': 'Edit API Key',

  '/clients': 'Resellers',
  '/clients/create': 'Create Reseller',
  '/clients/update/:id': 'Edit Reseller',

  '/companies': 'Companies',
  '/companies/create': 'Create Company',
  '/companies/update/:id': 'Edit Company',
  '/companies/view/:id': 'View Company',
  '/companies/role/:id': 'Company Roles',
  '/yardi-sync': 'Yardi Sync',

  '/branches': 'Branches',
  '/branches/create': 'Create Branch',
  '/branches/update/:id': 'Edit Branch',
  '/branch/view/:id': 'View Branch',
  '/branchnetwork/stats': 'Branch Network Stats',
  '/branchnetwork/stats/wifiusers/:id': 'Branch Wi-Fi Users',
  '/super-admin/data-export': 'System Data Export',

  '/users': 'Users',
  '/users/super-admins': 'Super Admin Users',
  '/users/create': 'Create User',
  '/users/update/:id': 'Edit User',
  '/users/update-password/:id': 'Change User Password',
  '/users/view/:id': 'View User',
  '/users/print': 'Print Users',
  '/users/legal-acceptance': 'Terms Acceptance',

  '/third-party-credentials': 'Third-Party Credentials',
  '/third-party-credentials/create': 'Add Credential',
  '/third-party-credentials/update/:id': 'Edit Credential',

  '/call-report': 'Call Report',

  // Categories (per type)
  '/:type/categories': 'Categories',
  '/:type/categories/create': 'Create Category',
  '/:type/categories/update/:id': 'Edit Category',
  '/:type/categories/share/:id': 'Share Category',

  // Knowledge base
  '/knowledge-base': 'Knowledge Base',
  '/knowledge-base/create': 'Create KB Post',
  '/knowledge-base/update/:id': 'Edit KB Post',
  '/knowledge-base/view': 'Knowledge Base',
  '/knowledge-base/view/post-:id': 'KB Post',
  '/knowledge-base/view/category-:id': 'KB Category',
  '/knowledge-base/manage/category-:id': 'Manage KB Category',
  '/knowledge-base/website': 'KB Website Menu',

  // Gallery
  '/gallery': 'Photo Gallery',
  '/gallery/create': 'Add Photos',
  '/gallery/update/:id': 'Edit Gallery',
  '/gallery/view/:id': 'View Gallery',

  // ISP / Providers
  '/manage-isp-provider': 'ISP Vendors',
  '/manage-isp-provider/create': 'Create ISP Vendor',
  '/manage-isp-provider/update/:id': 'Edit ISP Vendor',

  '/isp': 'ISP Management',
  '/isp/create': 'Create ISP',
  '/isp/update/:id': 'Edit ISP',
  '/isp/view/:id': 'View ISP',

  '/utility-providers': 'Utility Providers',
  '/utility-providers/create': 'Create Utility Provider',
  '/utility-providers/update/:id': 'Edit Utility Provider',
  '/utility-providers/view/:id': 'View Utility Provider',

  '/service-providers': 'Service Providers',
  '/service-providers/create': 'Create Service Provider',
  '/service-providers/update/:id': 'Edit Service Provider',
  '/service-providers/view/:id': 'View Service Provider',

  // Logs
  '/expect-scripts': 'Device Scripts',
  '/raddb-reports': 'Login Attempts',
  '/email-send-log': 'Email Log',
  '/devicelog': 'Device Logs',
  '/devicelog/:id': 'Device Log',
  '/dashboardlog': 'Dashboard Log',
  '/dashboardlog/activity/:id': 'Dashboard Activity',
  '/expansive-api-data': 'Expansive API Data',

  // Rack
  '/rackregistry': 'Rack Registry',
  '/rackregistry/create': 'Create Rack',
  '/rackregistry/update/:id': 'Edit Rack',
  '/rackregistry/view/:id': 'View Rack',
  '/rackregistry/upload/:id': 'Upload Rack Photos',

  '/rackdevice': 'Rack Devices',
  '/rackdevice/create': 'Create Rack Device',
  '/rackdevice/update/:id': 'Edit Rack Device',
  '/rackdevice/view/:id': 'View Rack Device',
  '/rackdevice/upload/:id': 'Upload Device Photos',

  // Time Tracker
  '/time-tracker': 'Time Tracker',
  '/time-tracker/projects': 'Time Tracker Projects',
  '/time-tracker/projects/create': 'Create Project',
  '/time-tracker/projects/update/:id': 'Edit Project',
  '/time-tracker/projects/view/:id': 'View Project',
  '/time-tracker/user-groups': 'Time Tracker User Groups',
  '/time-tracker/user-groups/create': 'Create User Group',
  '/time-tracker/user-groups/update/:id': 'Edit User Group',
  '/time-tracker/tracker': 'Time Tracker',
  '/time-tracker/tracker/project/:id': 'Tracker – Project',
  '/time-tracker/reports': 'Time Reports',

  // Time Tracker – Scheduling
  '/time-tracker/scheduling': 'Schedules',
  '/time-tracker/scheduling/create': 'Create Schedule',
  '/time-tracker/scheduling/update/:id': 'Edit Schedule',
  '/time-tracker/scheduling/:id/board': 'Weekly Schedule',
  '/time-tracker/scheduling/activities': 'Schedule Activities',

  // Billing System
  '/billing/dashboard': 'Billing Dashboard',
  '/billing/items': 'Items (Parts / Services)',
  '/billing/items/create': 'Add Item',
  '/billing/items/update/:id': 'Edit Item',
  '/billing/categories': 'Part / Service Categories',
  '/billing/categories/create': 'Add Category',
  '/billing/categories/update/:id': 'Edit Category',
  '/billing/tax-rates': 'Tax Rates',
  '/billing/tax-rates/create': 'Add Tax Rate',
  '/billing/tax-rates/update/:id': 'Edit Tax Rate',
  '/billing/invoices': 'Invoices',
  '/billing/invoices/create': 'Create Invoice',
  '/billing/invoices/update/:id': 'Edit Invoice',
  '/billing/invoices/view/:id': 'View Invoice',
  '/billing/recurring': 'Recurring Invoices',
  '/billing/recurring/create': 'New Recurring Template',
  '/billing/recurring/update/:id': 'Edit Recurring Template',
  '/billing/payments': 'Payments',
  '/billing/settings': 'Billing Settings',

  // Ticket System
  '/tickets/dashboard': 'Ticket Dashboard',
  '/tickets/projects': 'Ticket Projects',
  '/tickets/projects/create': 'New Ticket Project',
  '/tickets/projects/update/:id': 'Edit Ticket Project',
  '/tickets/projects/board/:id': 'Project Board',
  '/tickets/view/:id': 'View Ticket',
  '/tickets/all': 'All Tickets',

  // Sales
  '/sales': 'Sales Agents',
  '/sales/create': 'Create Sales Agent',
  '/sales/update/:id': 'Edit Sales Agent',
  '/sales_person': 'Sales Agents',

  // Workspace
  '/workspace': 'Workspace',
  '/workspace/create': 'Create Workspace',
  '/workspace/update/:id': 'Edit Workspace',
  '/workspace/:id/:slug/property': 'Workspace Properties',
  '/workspace/:id/:slug/property/create': 'Create Property',
  '/workspace/:id/:slug/property/update/:id': 'Edit Property',
  '/workspace/:id/:type/upload': 'Workspace Uploads',
  '/workspace/:id/:type/upload/create': 'Upload Workspace File',
  '/workspace/:id/:type/upload/update/:id': 'Edit Workspace Upload',
  '/workspace/:id/property/quotes/:id': 'Workspace Quotes',

  // Billing (dynamic routes file)
  '/billing': 'Billing',
  '/billing/product/index': 'Parts / Services',
  '/billing/product/create': 'Create Part / Service',
  '/billing/product/update/:id': 'Edit Part / Service',
  '/billing/invoice/index': 'Invoices',
  '/billing/invoice/create': 'Create Invoice',
  '/billing/invoice/update/:id': 'Edit Invoice',
  '/billing/invoice/view/:id': 'View Invoice',
  '/billing/transactions': 'Transactions',
  '/billing/settings': 'Billing Settings',

  // Website
  '/website': 'Website Management',
  '/website/page/index': 'Website Pages',
  '/website/page/create': 'Create Page',
  '/website/page/update/:id': 'Edit Page',
  '/website/page/view/:id': 'View Page',
  '/website/assets/index': 'Page Assets',
  '/website/assets/create': 'Create Page Asset',
  '/website/assets/update/:id': 'Edit Page Asset',
  '/website/assets/items/:id': 'Asset Items',
  '/website/menu': 'Menu Manager',

  // Network Management
  '/network-dashboard': 'Networking Dashboard',
  '/vlanregistry': 'VLAN Registry',
  '/vlanregistry/create': 'Create VLAN',
  '/vlanregistry/update/:id': 'Edit VLAN',

  '/deviceregistry': 'Device Registry',
  '/deviceregistry/create': 'Create Device',
  '/deviceregistry/update/:id': 'Edit Device',
  '/deviceregistry/view/:id': 'View Device',
  '/deviceregistry/deleted': 'Deleted Devices',

  '/ipregistry': 'IP Registry',
  '/ipregistry/create': 'Create IP',
  '/ipregistry/update/:id': 'Edit IP',

  '/officenumber': 'Office Management',
  '/officenumber/create': 'Create Office Number',
  '/officenumber/update/:id': 'Edit Office Number',

  '/portregistry': 'Switch Port Registry',
  '/portregistry/create': 'Create Port',
  '/portregistry/update/:id': 'Edit Port',

  '/officeregistry': 'Office Assignment',
  '/officeregistry/create': 'Create Office Assignment',
  '/officeregistry/update/:id': 'Edit Office Assignment',

  '/ipportregistry': 'IP Port Registry',
  '/ipportregistry/create': 'Create IP Port',
  '/ipportregistry/update/:id': 'Edit IP Port',

  '/wifigroup': 'Wi-Fi Groups',
  '/wifigroup/create': 'Create Wi-Fi Group',
  '/wifigroup/update/:id': 'Edit Wi-Fi Group',

  '/wifiusers': 'Wi-Fi Users',
  '/wifiusers/create': 'Create Wi-Fi User',
  '/wifiusers/update/:id': 'Edit Wi-Fi User',
  '/wifiusers/import': 'Import Wi-Fi Users',
  '/wifiusers/wifiusers-super-admin': 'Super Admin Wi-Fi Users',

  '/clientfirewall': 'Client Firewall',
  '/clientfirewall/create': 'Create Firewall Rule',
  '/clientfirewall/update/:id': 'Edit Firewall Rule',
  '/firewallports': 'Firewall Ports',

  '/branchnetwork': 'Branch Network',
  '/branchnetwork/create': 'Create Branch Network',
  '/branchnetwork/update/:id': 'Edit Branch Network',

  '/branchcustomer': 'Branch Clients',
  '/branchcustomer/create': 'Create Branch Client',
  '/branchcustomer/update/:id': 'Edit Branch Client',
  '/branchcustomer/bandwidth/:id': 'Branch Client Bandwidth',
  '/branchcustomer/print/:id': 'Print Branch Client',

  '/reports': 'Reports',

  '/map-ip-address-to-client': 'Public IP List',
  '/map-ip-address-to-client/create': 'Add Public IP',
  '/map-ip-address-to-client/update/:id': 'Edit Public IP',

  '/map-port-to-client': 'Public Port List',
  '/map-port-to-client/create': 'Add Public Port',
  '/map-port-to-client/update/:id': 'Edit Public Port',

  '/equipmenttype': 'Equipment Type',
  '/equipmenttype/create': 'Create Equipment Type',
  '/equipmenttype/update/:id': 'Edit Equipment Type',

  '/equipment-vendor': 'Equipment Vendor',
  '/equipment-vendor/create': 'Create Vendor',
  '/equipment-vendor/update/:id': 'Edit Vendor',

  '/equipment-model': 'Equipment Model',
  '/equipment-model/create': 'Create Model',
  '/equipment-model/update/:id': 'Edit Model',

  '/equipmentownership': 'Equipment Ownership',
  '/equipmentownership/create': 'Create Equipment Ownership',
  '/equipmentownership/update/:id': 'Edit Equipment Ownership',

  '/countries': 'Countries',
  '/countries/create': 'Create Country',
  '/countries/update/:id': 'Edit Country',

  '/states': 'States',
  '/states/create': 'Create State',
  '/states/update/:id': 'Edit State',

  '/bandwidth': 'Bandwidth',
  '/bandwidth/create': 'Create Bandwidth',
  '/bandwidth/update/:id': 'Edit Bandwidth',

  '/qosoptions': 'QoS Options',
  '/qosoptions/create': 'Create QoS Option',
  '/qosoptions/update/:id': 'Edit QoS Option',
};

// Build name for the current build theme. Always appears after the page title.
export const getBrandName = () => {
  switch (process.env.REACT_APP_THEME) {
    case 'NETWORK_COMMANDER':
      return 'Network Commander';
    case 'WORKSPACEGEEK':
      return 'WorkspaceGeek';
    case '640TECH':
      return '640 Tech';
    default:
      return 'Network Commander';
  }
};

// Heuristic: does this URL segment look like an id/slug placeholder?
const looksLikeId = (segment) => {
  if (!segment) return false;
  // Mongo ObjectId
  if (/^[0-9a-f]{24}$/i.test(segment)) return true;
  // Numeric id
  if (/^\d+$/.test(segment)) return true;
  // UUID
  if (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      segment,
    )
  ) {
    return true;
  }
  // Things that look like `post-123` / `category-64ab...`
  if (/^(post|category)-/.test(segment)) return true;
  return false;
};

// Replace id-looking segments with `:id` so we can match react-router style patterns.
const normalizePath = (pathname) => {
  const parts = pathname.split('/');
  return parts
    .map((part, i) => (i === 0 ? part : looksLikeId(part) ? ':id' : part))
    .join('/');
};

// Humanize a URL slug like `utility-providers` → `Utility Providers`.
const humanize = (slug) => {
  if (!slug) return '';
  return slug
    .replace(/[-_]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

/**
 * Resolve a title for an actual pathname. Never throws; always returns a
 * reasonable string.
 */
export const getPageTitle = (pathname) => {
  if (!pathname) return 'Dashboard';

  // Strip trailing slash (but keep root "/")
  const clean = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

  // 1) exact match
  if (ROUTE_TITLES[clean]) return ROUTE_TITLES[clean];

  // 2) normalize id-looking segments and match again
  const normalized = normalizePath(clean);
  if (ROUTE_TITLES[normalized]) return ROUTE_TITLES[normalized];

  // 3) walk up removing trailing segments
  const parts = normalized.split('/').filter(Boolean);
  for (let i = parts.length - 1; i >= 1; i--) {
    const candidate = '/' + parts.slice(0, i).join('/');
    if (ROUTE_TITLES[candidate]) return ROUTE_TITLES[candidate];
  }

  // 4) fallback: humanize the first segment
  if (parts[0]) return humanize(parts[0]);

  return 'Dashboard';
};

/**
 * Build the string that should go into `document.title`.
 * Page title first, then brand — so narrow tabs still show the page name.
 */
export const formatDocumentTitle = (pageTitle) => {
  const brand = getBrandName();
  if (!pageTitle) return brand;
  if (pageTitle === brand) return brand;
  return `${pageTitle} · ${brand}`;
};

/**
 * Convenience: set `document.title` from a pathname (or an explicit title).
 * Returns the string that was written.
 */
export const setDocumentTitleFromPath = (pathname, explicitTitle) => {
  const title = explicitTitle || getPageTitle(pathname);
  const finalTitle = formatDocumentTitle(title);
  if (typeof document !== 'undefined') {
    document.title = finalTitle;
  }
  return finalTitle;
};
